import { useCallback, useEffect, useRef } from "react";
import { useWsSubscription } from "hooks/useWsSubscription";
import type { WSEvent } from "../../../store/websocketStore";

// Bursts of events (slides fire many) collapse into one refetch — same
// debounce slides-streaming uses.
const REFETCH_DEBOUNCE_MS = 300;

/**
 * Refetches the publication whenever ANY event for this project arrives, so its
 * document path stays current without a manual refresh (the video no longer
 * depends on this — it reads project media instead, see MED-6969).
 * Deliberately unfiltered — narrowing to specific event types risks starving
 * the document on one the backend doesn't reliably fire. Any project activity
 * is a cheap, reliable signal that the publication may have changed.
 *
 * The document flash this could cause (a re-signed publication document url
 * reloading the PDF) is handled at the document layer — the document renders
 * from the project's own pinned documents (see `keepFirstDocuments`), not by
 * starving this refetch.
 */
export function useRefetchPublicationOnGeneration(
  projectId: string,
  refetch?: () => void
): void {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handler = useCallback(
    (event: WSEvent) => {
      if (event.project_id !== projectId) return;
      if (debounceRef.current !== null) {
        globalThis.clearTimeout(debounceRef.current);
      }
      debounceRef.current = globalThis.setTimeout(() => {
        debounceRef.current = null;
        refetch?.();
      }, REFETCH_DEBOUNCE_MS);
    },
    [projectId, refetch]
  );

  useEffect(
    () => () => {
      if (debounceRef.current !== null) {
        globalThis.clearTimeout(debounceRef.current);
      }
    },
    []
  );

  useWsSubscription({ type: "project", id: projectId }, handler);
}
